import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import ProjectNumbersApi from '../../../../../middleware/api/ProjectManagement/Projects/ProjectView/ProjectNumbers/ProjectNumbersApi'
import { LoadingState, ValidationError } from '../../../../../types/commonTypes'
import {
  AddChangeRequestNumbersProps,
  AddProjectNumbersProps,
  CRLookUpForNumbers,
  CurrencyListInfo,
  ProjectNumbersInfo,
  ProjectNumbersTabSliceState,
  UpdateProjectNumbers,
} from '../../../../../types/ProjectManagement/Project/ProjectView/ProjectNumbers/ProjectNumbersTypes'
import { ApiLoadingState } from '../../../../../middleware/api/apiList'
import { RootState } from '../../../../../stateStore'

const getProjectNumbers = createAsyncThunk(
  'projectNumbers/getProjectNumbers',
  async (projectid: number, thunkApi) => {
    try {
      return await ProjectNumbersApi.getProjectNumbers(projectid)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getCurrencyList = createAsyncThunk(
  'projectNumbers/getCurrencyList',
  async (_, thunkApi) => {
    try {
      return await ProjectNumbersApi.getCurrencyList()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const projectNumbers = createAsyncThunk(
  'projectNumbers/projectNumbers',
  async (props: AddProjectNumbersProps, thunkApi) => {
    try {
      return await ProjectNumbersApi.projectNumbers(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const changeRequestNumbers = createAsyncThunk(
  'projectNumbers/changeRequestNumbers',
  async (props: AddChangeRequestNumbersProps, thunkApi) => {
    try {
      return await ProjectNumbersApi.changeRequestNumbers(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getCRLookUpForNumbers = createAsyncThunk(
  'projectNumbers/getCRLookUpForNumbers',
  async (projectid: number, thunkApi) => {
    try {
      return await ProjectNumbersApi.getCRLookUpForNumbers(projectid)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const updateProjectNumbers = createAsyncThunk(
  'projectNumbers/updateProjectNumbers',
  async (props: UpdateProjectNumbers, thunkApi) => {
    try {
      return await ProjectNumbersApi.updateProjectNumbers(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const updateCRNumbers = createAsyncThunk(
  'projectNumbers/updateCRNumbers',
  async (props: UpdateProjectNumbers, thunkApi) => {
    try {
      return await ProjectNumbersApi.updateCRNumbers(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

export const initialProjectNumbersState: ProjectNumbersTabSliceState = {
  isLoading: ApiLoadingState.succeeded,
  projectNumbers: {} as ProjectNumbersInfo,
  currencyList: [],
  getCRLookUpForNumbers: [],
}
const ProjectNumbersSlice = createSlice({
  name: 'projectNumbers',
  initialState: initialProjectNumbersState,
  reducers: {},

  extraReducers: (builder) => {
    builder
      .addCase(getProjectNumbers.fulfilled, (state, action) => {
        state.projectNumbers = action.payload
        state.isLoading = ApiLoadingState.succeeded
      })
      .addCase(getProjectNumbers.pending, (state) => {
        state.isLoading = ApiLoadingState.loading
      })
      .addCase(getCRLookUpForNumbers.fulfilled, (state, action) => {
        state.getCRLookUpForNumbers = action.payload
        state.isLoading = ApiLoadingState.succeeded
      })
      .addCase(getCurrencyList.fulfilled, (state, action) => {
        state.currencyList = action.payload
        state.isLoading = ApiLoadingState.succeeded
      })
  },
})
const ProjectNumbersThunk = {
  getProjectNumbers,
  getCurrencyList,
  projectNumbers,
  getCRLookUpForNumbers,
  changeRequestNumbers,
  updateProjectNumbers,
  updateCRNumbers,
}

const isLoading = (state: RootState): LoadingState =>
  state.projectNumbers.isLoading

const projectNumbersData = (state: RootState): ProjectNumbersInfo =>
  state.projectNumbers.projectNumbers

const currencyList = (state: RootState): CurrencyListInfo[] =>
  state.projectNumbers.currencyList

const cRLookUpForNumbers = (state: RootState): CRLookUpForNumbers[] =>
  state.projectNumbers.getCRLookUpForNumbers

const invoiceClientReportSelectors = {
  isLoading,
  projectNumbersData,
  currencyList,
  cRLookUpForNumbers,
}

export const ProjectNumbersService = {
  ...ProjectNumbersThunk,
  actions: ProjectNumbersSlice.actions,
  selectors: invoiceClientReportSelectors,
}
export default ProjectNumbersSlice.reducer
